import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons";
import * as SelectPrimitive from "@radix-ui/react-select";
import React, { forwardRef } from "react";

import { cn } from "../cn";

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = forwardRef<React.ElementRef<typeof SelectPrimitive.Trigger>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { error?: boolean }>(function SelectTrigger(
  { className, error, children, ...props },
  ref,
) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      aria-invalid={error || undefined}
      className={cn(
        "tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-bg-input",
        "tw-px-3 tw-py-2 tw-font-sans tw-text-sm tw-text-foreground tw-shadow-xs tw-transition-colors",
        "data-[placeholder]:tw-text-muted-foreground [&>span]:tw-line-clamp-1 [&>span]:tw-text-left",
        "focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-offset-background",
        "disabled:tw-cursor-not-allowed disabled:tw-opacity-50",
        error ? "tw-border-destructive-border focus:tw-ring-ring-error" : "tw-border-border focus:tw-ring-ring",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="tw-size-4 tw-opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});

export const SelectContent = forwardRef<React.ElementRef<typeof SelectPrimitive.Content>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>>(function SelectContent(
  { className, children, position = "popper", ...props },
  ref,
) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        data-redesign-v1
        position={position}
        className={cn(
          "tw-relative tw-z-50 tw-max-h-96 tw-min-w-[8rem] tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-border",
          "tw-bg-card tw-font-sans tw-text-card-foreground tw-shadow-md",
          position === "popper" && "data-[side=bottom]:tw-translate-y-1 data-[side=top]:-tw-translate-y-1",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton className="tw-flex tw-h-6 tw-items-center tw-justify-center">
          <ChevronUpIcon className="tw-size-4" />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport className={cn("tw-p-1", position === "popper" && "tw-h-[var(--radix-select-trigger-height)] tw-w-full tw-min-w-[var(--radix-select-trigger-width)]")}>
          {children}
        </SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="tw-flex tw-h-6 tw-items-center tw-justify-center">
          <ChevronDownIcon className="tw-size-4" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});

export const SelectItem = forwardRef<React.ElementRef<typeof SelectPrimitive.Item>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>>(function SelectItem(
  { className, children, ...props },
  ref,
) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded tw-py-1.5 tw-pl-8 tw-pr-2 tw-text-sm tw-outline-none",
        "focus:tw-bg-border focus:tw-text-accent-foreground data-[disabled]:tw-pointer-events-none data-[disabled]:tw-opacity-50",
        className,
      )}
      {...props}
    >
      <span className="tw-absolute tw-left-2 tw-flex tw-size-3.5 tw-items-center tw-justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="tw-size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});

export const SelectLabel = forwardRef<React.ElementRef<typeof SelectPrimitive.Label>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>>(function SelectLabel({ className, ...props }, ref) {
  return <SelectPrimitive.Label ref={ref} className={cn("tw-px-2 tw-py-1.5 tw-text-xs tw-font-medium tw-text-muted-foreground", className)} {...props} />;
});
